import React, { useContext, useEffect, useRef, useState } from "react";
import "./App.css";

const ROOT = "https://slides.die-buecherheimat.de";
const TRANSITION_TIME = 500;

const READ_TOKEN = "c11c98568fdcc8343a5a05390e67e713e139e0872f8746744166c3ac2bed67f49722921bcf17d5807f802cafeb04a955b2c0cd3579c3a82fc2f2241adbc9826aead5493196615b2a78cc638ce6576e42aec47409dd5e012c4043036e97201e272202e92b8e75b9283f5e97247affa2b279159d52522ab85cafdb08c22197d682";

const authFetch = (input: string, callback: (error: Error | null, data?: any) => void, init?: any) => {
  const xhr = new XMLHttpRequest();
  xhr.open(init?.method || "GET", input, true);
  xhr.setRequestHeader("Authorization", `Bearer ${READ_TOKEN}`);
  if (init?.headers) {
    for (const key in init.headers) {
      xhr.setRequestHeader(key, init.headers[key]);
    }
  }
  xhr.onreadystatechange = function () {
    if (xhr.readyState === 4) {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          callback(null, data);
        } catch (e) {
          callback(new Error("Failed to parse response"));
        }
      } else {
        callback(new Error(`Request failed with status ${xhr.status}`));
      }
    }
  };
  xhr.onerror = function () {
    callback(new Error("Network error"));
  };
  xhr.send(init?.body);
};

interface Config {
  slideshow: Slideshow;
  puzzle: Puzzle | undefined;
}

interface Puzzle {
  id: string;
  Image: MediaObject;
  PuzzleImage: MediaObject;
  updatedAt: string;
}

interface Slide {
  Type: "Picture" | "ArchivePicture" | "Custom";
  Title: string;
  TimeInSeconds: number;
  ArchivePictureId: string | null;
  ArchiveInfo: { url: string; description: string; title: string } | null;
  CustomType: "puzzle" | null;
  File: MediaObject;
  id: number;
}

interface MediaObject {
  url: string;
  alternativeText: string;
}

interface Slideshow {
  Slides: Slide[];
  Title: string;
  updatedAt: string;
  id: string;
}

const PuzzleContext = React.createContext<
  [string, React.Dispatch<React.SetStateAction<string>>]
>(["", () => {}]);

function App() {
  const [slideshow, setSlideShow] = useState<Slideshow | null>(null);
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const slideshowRef = useRef<Slideshow | null>(null);
  const puzzleRef = useRef<Puzzle | null>(null);
  const [error, setError] = useState("");
  const [puzzleUpdateStamp, setPuzzleUpdateStamp] = useState("");

  useEffect(() => {
    const load = () => {
      const previewId = new URL(window.location.href).searchParams?.get("id");
      
      if (previewId) {
        authFetch(
          `${ROOT}/api/slideshows/${previewId}?populate[0]=Slides&populate[1]=Slides.File`,
          (err, data) => {
            if (err) {
              setError(`Error loading slideshow: ${err.message}`);
              return;
            }
            handleConfig({ slideshow: data?.data, puzzle: undefined });
          }
        );
      } else {
        authFetch(
          `${ROOT}/api/bucherheimat?populate[0]=slideshow&populate[1]=slideshow.Slides&populate[2]=slideshow.Slides.File&populate[3]=puzzle&populate[4]=puzzle.PuzzleImage`,
          (err, data) => {
            if (err) {
              setError(`Error loading config: ${err.message}`);
              return;
            }
            handleConfig(data?.data);
          }
        );
      }
    };

    const handleConfig = (config: Config | null) => {
      if (!config) {
        setError(`Error loading slideshow`);
        return;
      }

      const slideshow = config.slideshow;

      if (
        config.puzzle &&
        (!puzzleRef.current ||
          puzzleRef.current.id !== config.puzzle.id ||
          puzzleRef.current.updatedAt !== config.puzzle.updatedAt)
      ) {
        console.log(config.puzzle.updatedAt);
        puzzleRef.current = config.puzzle;
        setPuzzleUpdateStamp(config.puzzle.updatedAt);
        setPuzzle(config.puzzle);
      }

      if (
        !slideshowRef.current ||
        slideshowRef.current.id !== slideshow.id ||
        slideshowRef.current.updatedAt !== slideshow.updatedAt
      ) {
        const archiveSlides = slideshow.Slides.filter(
          (slide) => slide.Type === "ArchivePicture" && slide.ArchivePictureId
        );
        
        if (archiveSlides.length === 0) {
          slideshowRef.current = slideshow;
          setSlideShow(slideshow);
          return;
        }

        let completedCount = 0;
        const totalCount = archiveSlides.length;
        
        archiveSlides.forEach((slide) => {
          const id = slide.ArchivePictureId!.match(/\d+$/)![0];
          authFetch(
            `https://bp.bad-harzburg-stiftung.de/api/api/pictures/${id}?populate[0]=media&populate[1]=descriptions&populate[2]=title`,
            (err, data) => {
              if (!err && data?.data) {
                const json = data.data;
                slide.ArchiveInfo = {
                  url: json.File.url,
                  title: json.title,
                  description: json.descriptions,
                };
              }
              completedCount++;
              if (completedCount === totalCount) {
                slideshowRef.current = slideshow;
                setSlideShow(slideshow);
              }
            }
          );
        });
      }
    };

    const intervalId = setInterval(() => load(), 20000);
    load();

    return () => clearInterval(intervalId);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement!.requestFullscreen();
    }
  };

  return (
    <PuzzleContext.Provider value={[puzzleUpdateStamp, setPuzzleUpdateStamp]}>
      <div className="slideshow" onClick={toggleFullscreen}>
        {slideshow ? (
          <SlideshowDisplay slideshow={slideshow} puzzle={puzzle} />
        ) : !error ? (
          <div>Bilder werden geladen...</div>
        ) : (
          <div>{error}</div>
        )}
      </div>
    </PuzzleContext.Provider>
  );
}

function SlideshowDisplay({
  slideshow,
  puzzle,
}: {
  slideshow: Slideshow;
  puzzle: Puzzle | null;
}) {
  const contentForSlide = (slide: Slide) => {
    switch (slide.Type) {
      case "ArchivePicture":
        return slide.ArchiveInfo?.url ? (
          <>
            <div className="archive-info">
              <div className="archive-title">{slide.ArchiveInfo.title}</div>
              <div className="archive-description">
                {slide.ArchiveInfo.description}
              </div>
            </div>
            <img
              alt={slide.ArchiveInfo.title}
              src={`https://bp.bad-harzburg-stiftung.de/${slide.ArchiveInfo.url}`}
            />
          </>
        ) : null;
      case "Picture":
        return (
          <img
            alt={slide.File.alternativeText}
            src={`${ROOT}${slide.File.url}`}
          />
        );
      case "Custom":
        return puzzle ? (
          <PuzzleView url={puzzle.PuzzleImage.url} />
        ) : (
          <div>no puzzle set</div>
        );
      default:
        return <span>Unknown type</span>;
    }
  };
  const [activeIndex, setActiveIndex] = useState(0);
  const maxSlides = slideshow.Slides.length;
  const slide = slideshow.Slides[activeIndex];
  const nextSlide = slideshow.Slides[(activeIndex + 1) % maxSlides];

  useEffect(() => {
    if (slide) {
      const timeoutId = setTimeout(() => {
        setActiveIndex((previousIndex) => {
          return (previousIndex + 1) % maxSlides;
        });
      }, slide.TimeInSeconds * 1000 - TRANSITION_TIME);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [slide, maxSlides]);

  // dirty animation code
  return maxSlides > 0 ? (
    <div className="slick-track transitioning" key={activeIndex}>
      <div className="slide">{contentForSlide(slide)}</div>
      <div className="slide next">{contentForSlide(nextSlide)}</div>
    </div>
  ) : (
    <span>no slide</span>
  );
}

function PuzzleView({ url }: { url: string }) {
  const [puzzleStamp] = useContext(PuzzleContext);
  return <img src={`${ROOT}${url}?stamp=${puzzleStamp}`} alt="puzzle" />;
}

export default App;
